/** @format */

import React from "react";
import { NavLink } from "react-router-dom";
import { useLocale } from "../i18n/hooks";
import { ReactComponent as Trophy } from "./../../assets/icons/trophy.svg";
import { useTranslation } from "react-i18next";
import LevelBanners from "../levelBanners/LevelBanners";

import "./DashboardCard.scss";
import {
  lessonProgress,
  selectLessonsProgress,
} from "../lessonProgress/processSlice";
import { useAppSelector } from "../../app/hooks";
import { Lesson } from "../resources/types";
import FormattedText from "../formattedText/FormattedText";

type Props = {
  lesson: Lesson;
  index: number;
};

const DashboardCard: React.FC<Props> = ({ lesson, index }) => {
  const locale = useLocale();

  const lessonsProgress = useAppSelector(selectLessonsProgress());
  const progress: Number | undefined = lessonsProgress.find(
    (lessonProgress: lessonProgress) =>
      lessonProgress.lessonUUID === lesson.uuid,
  )?.progress;
  const progressPercentage =
    ((Number(progress ?? -1) + 1) / lesson?.subLessons.length) * 100;

  const { t: _ } = useTranslation();

  const progressClass =
    progressPercentage === 0
      ? "danger"
      : progressPercentage <= 50
      ? "warning"
      : progressPercentage < 100
      ? "primary"
      : "success";

  const heroImagePosition =
    typeof lesson.heroImage === "object"
      ? lesson.heroImage.$metadata?.positionY
      : undefined;
  let heroImage =
    typeof lesson.heroImage === "object"
      ? lesson.heroImage.src
      : lesson.heroImage;
  const lessonTitle =
    typeof lesson.title === "object" ? lesson.title?.[locale] : lesson?.title;
  const heroImageAltText =
    typeof lesson.heroImage === "object"
      ? lesson.heroImage.$metadata?.alt
      : undefined;

  return (
    <>
      <div
        className="card-header text-center text-white d-flex flex-column flex-lg-row align-items-center justify-content-between p-0 "
        role="img"
        title={heroImageAltText}
        style={{
          backgroundPositionY: `${heroImagePosition ?? 50}%`,
          backgroundImage: `url("${heroImage}${
            heroImage.includes("images.unsplash.com") ? "&fm=jpg&w=1200" : ""
          }")`,
        }}
      >
        <div className="lesson-title flex-grow-1 w-auto mx-5" >
          <h5 className="card-title my-5 mx-auto py-4 px-5" title={''}>
            <FormattedText html={lessonTitle ?? ""} />
          </h5>
        </div>
        <LevelBanners levels={lesson.difficultyLevels} />
      </div>
      <div className="card-body px-4 d-flex flex-wrap gap-3 justify-content-between align-items-center flex-column flex-lg-row text-center">
        {progressPercentage === 0 ? (
          <span>{_("You haven't started this lesson yet.")}</span>
        ) : progressPercentage <= 50 ? (
          <span>
            {_(
              "You have made a start to this lesson but are yet to complete it.",
            )}
          </span>
        ) : progressPercentage < 100 ? (
          <span>{_("You have almost completed this lesson.")}</span>
        ) : (
          <span>
            {_("You have successfully completed all of this lesson.")}
          </span>
        )}
        <div className="">
          {progressPercentage === 100 ? (
            <Trophy title="Completed Lesson" className="trophy" />
          ) : (
            <NavLink
              to={`${index}/${Number(progress ?? 0)}`}
              className={`btn btn-${progressClass}`}
            >
              {progressPercentage === 0 ? _("Begin") : _("Continue")}
            </NavLink>
          )}
        </div>
      </div>
      <div className="card-footer p-0">
        <div
          className={`progress progress-${progressClass} rounded-bottom`}
          style={{ height: "4px" }}
        >
          <div
            className={`progress-bar bg-${progressClass}`}
            role="progressbar"
            aria-label={progressClass}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPercentage}
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>
    </>
  );
};

export default DashboardCard;
